import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import Progress from 'components/common/Progress';
import Button from 'components/common/Button';
import DatepickerField from 'components/fields/DatepickerField';
import TextField from 'components/fields/TextField';
import Badge from 'components/common/Badge';
import IconUndo from 'components/icons/IconUndo';

import { updateOrderLines } from 'redux/modules/ordersLines';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';

import dateFormat from 'config/dateFormat';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';
import hexToRgb from 'styles/hexToRgb';

const styles = theme => ({
  root: {
    position: 'relative',
    width: 300,
  },
  grid: {
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
  button: {
    minWidth: 107,
    marginTop: 5,
  },
  badgeInsurance: {
    display: 'inline-block',
    marginRight: 4,
  },
  typographyTitle: {
    fontWeight: 600,
  },
  typographyTitleRecoup: {
    color: theme.palette.validation[500],
  },
  typographyTitleRebill: {
    color: theme.palette.add[500],
  },
  typographyLabel: {
    marginBottom: 12,
    fontSize: pxToRem(12),
    letterSpacing: pxToEm(0.5),
    fontWeight: 700,
    color: theme.palette.grey[600],
  },
  fieldRecoup: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.validation[500])}, 0.2)`,
  },
  fieldRebill: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.add[500])}, 0.2)`,
  },
  iconRedo: {
    display: 'inline-block',
    width: 20,
    height: 20,
    color: theme.palette.add[500],
    transform: 'scaleX(-1)',
    verticalAlign: 'text-bottom',
  },
  iconUndo: {
    display: 'inline-block',
    width: 20,
    height: 20,
    color: theme.palette.validation[500],
    verticalAlign: 'text-bottom',
  },
});

function onSubmit(values, dispatch, props) {
  return new Promise((resolve, reject) => {
    dispatch(updateOrderLines({
      values: [{
        ID: props.orderLineID,
        orderID: props.orderID,
        'Check Book Sheet': {
          [`insuranceID-${props.insurance.ID}`]: {
            'Check Number': values['Check Number'],
          },
        },
      }],
      resolve,
      reject,
      showNotification: false,
    }));
  })
  .then(res => new Promise((resolve, reject) => {
    dispatch(updateOrderLines({
      values: [{
        ID: props.orderLineID,
        orderID: props.orderID,      
        orderLineInsuranceParam: {
          [`insuranceID-${props.insurance.ID}`]: {
            Reimbs: props.variant === 'recoup' ? values['Recoup'].replace('$', '') : values['Rebill'].replace('$', ''),
            'Reimbs Date': props.variant === 'recoup' ? values['Recoup Date'] : values['Rebill Date'],
            'Order Line ID': props.orderLineID,
            'Insurance ID': props.insurance.ID,
            'Check Book Sheet ID': res[0]['Check Book Sheet'][0].ID,
          },
        },
      }],
      resolve,
      reject,
    }));
  }))
  .then(res => res,
    (err) => {
      console.log(err);
      throw new SubmissionError(err);
    });
}

function validateForm(values) {
  const errors = {};
  if (!values['Assigned User ID'] || values['Assigned User ID'].length === 0) {
    errors['User Name'] = 'Please, select user from list';
  }
  if (values['Driver Name']
    && values['Driver Name'].length > 0
    && values['Driver Name'] !== values['Selected Driver Name']) {
    errors['Driver Name'] = 'Please, select driver from list';
  }
  return errors;
}

function onChange(values, dispatch, props) {
  if (values['Selected User Name'] !== values['User Name']) {
    props.change('Assigned User ID', '');
  }
  if (values['Selected Driver Name'] !== values['Driver Name']) {
    props.change('Driver ID', '');
  }
}

const OrdersTableRowItemRecoupForm = ({
  variant,
  handleSubmit,
  submit,
  className,
  submitting,
  insurance,
  onCancel,
  classes,
}) => (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
    className={classNames(className, classes.root)}
  >
    <Grid
      container
      justify="center"
      spacing={24}
    >
      <Grid item>
        {variant === 'recoup' && (
          <Typography
            gutterBottom
            className={classNames(classes.typographyTitle, classes.typographyTitleRecoup)}
          >
            <IconUndo className={classes.iconUndo} /> Add Recoupment:
          </Typography>
        )}
        {variant === 'rebill' && (
          <Typography
            gutterBottom
            className={classNames(classes.typographyTitle, classes.typographyTitleRebill)}
          >
            <IconUndo className={classes.iconRedo} /> Rebill and Pay:
          </Typography>
        )}
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={16}
    >
      <Grid
        item
        xs={6}
      >
        <Field
          {...(variant === 'recoup' ? {
            id: 'Recoup',
            name: 'Recoup',
            label: 'Recoup Amount',
            inputRootClassName: classes.fieldRecoup,
            normalize: normalize.negativePrice,
          } : {
            id: 'Rebill',
            name: 'Rebill',
            label: 'Rebill Amount',
            inputRootClassName: classes.fieldRebill,
            normalize: normalize.price,
          })}
          component={TextField}
          validate={[validate.required]}
          size="sm"
          color="light"
          errorPlacement="left"
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={6}
      >
        <Field
          {...(variant === 'recoup' ? {
            id: 'Recoup Date',
            name: 'Recoup Date',
            label: 'Recoup Date',
          } : {
            id: 'Rebill Date',
            name: 'Rebill Date',
            label: 'Rebill Date',
          })}
          component={DatepickerField}
          validate={[validate.date, validate.required]}
          normalize={normalize.date}
          minDate="01/01/1900"
          maxDate="01/01/2100"
          dateFormat={dateFormat}
          placeholder="mm/dd/yyyy"
          maxLength="10"
          size="sm"
          color="light"
          errorPlacement="left"
          fullWidth
          endIcon="start"
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={16}
    >
      <Grid
        item
        xs={6}
      >
        <Typography className={classes.typographyLabel}>
          Insurance
        </Typography>
        <Typography>
          <Badge
            className={classes.badgeInsurance}
            color="primary"
            tooltipText={insurance['Order Benefits']}
          >
            {insurance['Order Benefits'] === 'Primary' && '1'}
            {insurance['Order Benefits'] === 'Secondary' && '2'}
            {insurance['Order Benefits'] === 'Secondary As Primary' && '2'}
            {insurance['Order Benefits'] === 'Tertiary' && '3'}
            {insurance['Insurance Type'] === 'Self' && 'S'}
            {(!insurance['Order Benefits'] && insurance['Insurance Type'] !== 'Self')
              && insurance['Payer Name'].charAt(0)}
          </Badge>
          {insurance['Payer Name']}
        </Typography>
      </Grid>
      <Grid
        item
        xs={6}
      >
        <Field
          id="Check Number"
          name="Check Number"
          label="Check Number"
          component={TextField}
          validate={[validate.required]}
          size="sm"
          color="light"
          errorPlacement="left"
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      container
      justify="center"
      spacing={16}
    >
      <Grid
        item
      >
        <Button
          className={classes.button}
          variant="raised"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </Grid>
      <Grid
        item
      >
        <Button
          className={classes.button}
          variant="raised"
          color="primary"
          onClick={submit}
          disabled={submitting}
        >
          Done
        </Button>
      </Grid>
    </Grid>
    {submitting &&
      <Progress />
    }
  </form>
);

OrdersTableRowItemRecoupForm.propTypes = {
  variant: PropTypes.oneOf([
    'recoup',
    'rebill',
  ]).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  className: PropTypes.string,
  submitting: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    grid: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
  }).isRequired,
};

OrdersTableRowItemRecoupForm.defaultProps = {
  className: '',
  variant: 'recoup',
};

export default reduxForm({
  onSubmit,
  onChange,
  validate: validateForm,
})(withStyles(styles)(OrdersTableRowItemRecoupForm));



// WEBPACK FOOTER //
// ./src/components/workflow/OrdersTable/OrdersTableRowItemRecoupForm.js